'use client'

import * as React from 'react'
import Image from 'next/image'
import { domMax, LazyMotion, m, animate } from 'framer-motion'
import type { AnimationPlaybackControls } from 'framer-motion'
import { ChevronLeftIcon, ChevronRightIcon } from 'primitives/icons'

interface ComparisonSliderProps {
  beforeSrc: string
  afterSrc: string
  beforeLabel: string
  afterLabel: string
  className?: string
}

function useSliderDrag(snapPoints: number[] = []) {
  const constraintsRef = React.useRef<HTMLDivElement>(null)
  const barRef = React.useRef<HTMLDivElement>(null)
  const [sliderValue, setSliderValue] = React.useState(50)
  const [containerWidth, setContainerWidth] = React.useState(0)
  const isDragging = React.useRef(false)
  const snapAnimationRef = React.useRef<AnimationPlaybackControls | null>(null)

  React.useEffect(() => {
    const el = constraintsRef.current
    if (!el) {
      return
    }
    const observer = new ResizeObserver(([entry]) => {
      if (entry) {
        setContainerWidth(entry.contentRect.width)
      }
    })
    observer.observe(el)
    return () => observer.disconnect()
  }, [])

  React.useEffect(
    () => () => {
      snapAnimationRef.current?.stop()
    },
    [],
  )

  const getCurrentPercentFromViewport = React.useCallback(() => {
    const rect = constraintsRef.current?.getBoundingClientRect()
    const barPosition = barRef.current?.getBoundingClientRect()
    const newPosition = barPosition?.left ? barPosition.left + 2 : 0
    const left = rect?.left ?? 0
    const length = rect?.width ?? 1
    const nextValue = Math.max(0, Math.min(1, (newPosition - left) / length))
    if (parseFloat(nextValue.toFixed(2)) === 0) {
      return 0
    }
    if (parseFloat(nextValue.toFixed(3)) > 0.998) {
      return 100
    }
    return nextValue * 100
  }, [])

  const onDrag = React.useCallback(() => {
    isDragging.current = true
    snapAnimationRef.current?.stop()
    const percent = getCurrentPercentFromViewport()
    setSliderValue(Math.min(Math.max(percent, 0), 100))
  }, [getCurrentPercentFromViewport])

  const onDragEnd = React.useCallback(() => {
    isDragging.current = false
    if (!snapPoints.length) {
      return
    }
    const current = getCurrentPercentFromViewport()
    const nearest = snapPoints.reduce((closest, point) =>
      Math.abs(point - current) < Math.abs(closest - current) ? point : closest,
    )
    snapAnimationRef.current?.stop()
    snapAnimationRef.current = animate(current, nearest, {
      type: 'spring',
      stiffness: 400,
      damping: 32,
      onUpdate: value => setSliderValue(value),
    })
  }, [snapPoints, getCurrentPercentFromViewport])

  const x = containerWidth
    ? sliderValue > 99.998
      ? containerWidth - 2
      : (sliderValue / 100) * containerWidth - 2
    : 0

  return { constraintsRef, barRef, sliderValue, x, isDragging, onDrag, onDragEnd }
}

export function ComparisonSlider({
  beforeSrc,
  afterSrc,
  beforeLabel,
  afterLabel,
  className = '',
}: ComparisonSliderProps) {
  const { constraintsRef, barRef, sliderValue, x, isDragging, onDrag, onDragEnd } = useSliderDrag([
    0, 50, 100,
  ])

  const animateX: Record<string, number> = { x }

  return (
    <div
      ref={constraintsRef}
      className={`relative aspect-[748/320] overflow-hidden rounded-lg bg-[#111] ${className}`}
    >
      {/* Before (static image) */}
      <div
        className="absolute inset-0 select-none overflow-hidden"
        style={{ clipPath: `inset(0 ${100 - sliderValue}% 0 0)` }}
      >
        <Image
          alt=""
          src={beforeSrc}
          fill
          sizes="(min-width: 1536px) 748px, (min-width: 1024px) 600px, 100vw"
          className="pointer-events-none object-cover"
          draggable={false}
        />
      </div>

      {/* After (video) */}
      <div
        className="absolute inset-0 select-none overflow-hidden bg-[#111]"
        style={{ clipPath: `inset(0 0 0 ${sliderValue}%)` }}
      >
        <video
          autoPlay
          loop
          muted
          playsInline
          src={afterSrc}
          className="pointer-events-none size-full object-cover"
          draggable={false}
          onError={e => e.stopPropagation()}
        />
      </div>

      {/* Before label */}
      <div
        className="pointer-events-none absolute inset-0 overflow-hidden"
        style={{ clipPath: `inset(0 ${100 - sliderValue}% 0 0)` }}
      >
        <div
          className="absolute bottom-3 -translate-x-1/2 whitespace-nowrap"
          style={{ left: `${sliderValue / 2}%` }}
        >
          <span className="rounded-full bg-cf-black/60 px-3 py-1 text-xs font-medium text-cf-white backdrop-blur-sm">
            {beforeLabel}
          </span>
        </div>
      </div>

      {/* After label */}
      <div
        className="pointer-events-none absolute inset-0 overflow-hidden"
        style={{ clipPath: `inset(0 0 0 ${sliderValue}%)` }}
      >
        <div
          className="absolute bottom-3 -translate-x-1/2 whitespace-nowrap"
          style={{ left: `${sliderValue + (100 - sliderValue) / 2}%` }}
        >
          <span className="rounded-full bg-cf-black/60 px-3 py-1 text-xs font-medium text-cf-white backdrop-blur-sm">
            {afterLabel}
          </span>
        </div>
      </div>

      {/* Drag handle */}
      <LazyMotion features={domMax}>
        <m.div
          ref={barRef}
          drag="x"
          dragElastic={false}
          dragConstraints={constraintsRef}
          transition={{ duration: 0 }}
          dragMomentum={false}
          initial={animateX}
          animate={isDragging.current ? undefined : animateX}
          onDrag={onDrag}
          onDragEnd={onDragEnd}
          className="relative flex h-full w-[4px] cursor-grab select-none flex-col active:cursor-grabbing"
        >
          <div className="h-full w-1 bg-cf-white/90 shadow-sm">
            <div className="absolute left-1/2 top-1/2 flex size-8 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full border border-gray-300 bg-cf-white shadow-md">
              <ChevronLeftIcon className="size-4 -mr-1" />
              <ChevronRightIcon className="size-4 -ml-1" />
            </div>
          </div>
        </m.div>
      </LazyMotion>
    </div>
  )
}
